import React, { useState } from 'react';
import { Truck, Ship, Building2, ShoppingBag, Car, Package } from 'lucide-react';
import { industryContent } from '../data/industryContent';
interface IndustriesPageProps {
  isDark: boolean;
}
const industries = [{
  id: 'transport',
  icon: Truck,
  title: 'Transport & Logistics'
}, {
  id: 'shipping',
  icon: Ship,
  title: 'Shipping & Freight'
}, {
  id: 'hospitality',
  icon: Building2,
  title: 'Hospitality'
}, {
  id: 'retail',
  icon: ShoppingBag,
  title: 'Retail'
}, {
  id: 'automobile',
  icon: Car,
  title: 'Automobile'
}, {
  id: 'manufacturing',
  icon: Package,
  title: 'Manufacturing'
}];
export function IndustriesPage({
  isDark
}: IndustriesPageProps) {
  const [selectedIndustry, setSelectedIndustry] = useState('transport');
  const currentContent = industryContent[selectedIndustry as keyof typeof industryContent];
  return <div className="page-transition pt-32 pb-16 px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-310">
      <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-311">
        <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-312">
          Industries We Serve
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300" data-id="element-313">
          Specialized solutions tailored to the unique challenges of your
          sector.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-314">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-4 space-y-3" data-id="element-315">
          {industries.map(industry => {
          const Icon = industry.icon;
          const isActive = selectedIndustry === industry.id;
          return <button key={industry.id} onClick={() => setSelectedIndustry(industry.id)} className={`
                  w-full flex items-center gap-4 p-5 rounded-2xl transition-all duration-300 text-left
                  ${isActive ? 'bg-brand-50 dark:bg-brand-900/20 shadow-sm' : 'bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800'}
                  border ${isActive ? 'border-brand-200 dark:border-brand-800' : 'border-gray-200 dark:border-gray-700'}
                `} data-id="element-316">
                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors
                  ${isActive ? 'bg-brand-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}
                `} data-id="element-317">
                  <Icon className="w-6 h-6" strokeWidth={2} data-id="element-318" />
                </div>
                <span className={`
                  text-lg font-semibold transition-colors
                  ${isActive ? 'text-brand-700 dark:text-brand-300' : 'text-gray-700 dark:text-gray-300'}
                `} data-id="element-319">
                  {industry.title}
                </span>
              </button>;
        })}
        </div>

        {/* Content Panel */}
        <div className="lg:col-span-8" data-id="element-320">
          <div className="glass-panel rounded-2xl p-8 md:p-10 min-h-[600px]" data-id="element-321">
            <h2 className="text-3xl font-bold mb-6" data-id="element-322">{currentContent.title}</h2>

            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 leading-relaxed" data-id="element-323">
              {currentContent.description}
            </p>

            <div className="mb-10" data-id="element-324">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2" data-id="element-325">
                <div className="w-1 h-6 bg-brand-500 rounded-full" data-id="element-326"></div>
                Key Features
              </h3>
              <div className="space-y-4" data-id="element-327">
                {currentContent.features.map((feature, index) => <div key={index} className="flex items-start gap-3 group" data-id="element-328">
                    <div className="w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900/50 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-329">
                      <div className="w-2 h-2 rounded-full bg-brand-600 dark:bg-brand-400" data-id="element-330" />
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed" data-id="element-331">
                      {feature}
                    </p>
                  </div>)}
              </div>
            </div>

            <div className="mb-10" data-id="element-332">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2" data-id="element-333">
                <div className="w-1 h-6 bg-brand-500 rounded-full" data-id="element-334"></div>
                Business Benefits
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-335">
                {currentContent.benefits.map((benefit, index) => <div key={index} className="p-4 bg-brand-50 dark:bg-brand-900/30 rounded-xl border border-brand-100 dark:border-brand-800" data-id="element-336">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200" data-id="element-337">
                      {benefit}
                    </p>
                  </div>)}
              </div>
            </div>

            {currentContent.useCases && <div data-id="element-338">
                <h3 className="text-xl font-bold mb-6 flex items-center gap-2" data-id="element-339">
                  <div className="w-1 h-6 bg-brand-500 rounded-full" data-id="element-340"></div>
                  Common Use Cases
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-341">
                  {currentContent.useCases.map((useCase, index) => <div key={index} className="px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-342">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-343">
                        {useCase}
                      </p>
                    </div>)}
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}